import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Send, Image as ImageIcon, MapPin, ArrowLeft, Shield, Lock } from "lucide-react";
import { chatAPI, uploadImage, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import LocationPicker from "../components/LocationPicker";
import LocationMessage from "../components/LocationMessage";

export default function Chat() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { cid } = useParams();
  const [conversations, setConversations] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const endRef = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    chatAPI.list().then(setConversations).catch(() => setConversations([]));
  }, [user, navigate, authLoading]);

  useEffect(() => {
    if (!cid) { setCurrent(null); setMessages([]); return; }
    chatAPI.get(cid).then(setCurrent).catch(() => setCurrent(null));
    chatAPI.messages(cid).then(setMessages).catch(() => setMessages([]));
    const t = setInterval(() => {
      chatAPI.messages(cid).then((m) => setMessages(prev => m.length !== prev.length ? m : prev)).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [cid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendText = async () => {
    if (!text.trim() || !cid) return;
    setSending(true);
    try {
      const msg = await chatAPI.send(cid, { type: "text", text });
      setMessages(prev => [...prev, msg]);
      setText("");
      if (msg.censored) toast.info("Alcuni dati personali sono stati automaticamente oscurati per la tua sicurezza.");
    } catch (e) {
      toast.error("Errore invio");
    } finally { setSending(false); }
  };

  const sendImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !cid) return;
    setSending(true);
    try {
      const url = await uploadImage(file);
      const msg = await chatAPI.send(cid, { type: "image", image_url: url });
      setMessages(prev => [...prev, msg]);
    } catch (err) {
      toast.error("Errore upload foto");
    } finally { setSending(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const sendLocation = async (loc) => {
    if (!cid) return;
    try {
      const msg = await chatAPI.send(cid, { type: "location", location: loc });
      setMessages(prev => [...prev, msg]);
    } catch (e) { toast.error("Errore invio posizione"); }
  };

  if (!user) return null;
  if (authLoading) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-slate-500">Caricamento...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-black text-[#1d3d3a]">Messaggi</h1>
        <p className="text-xs text-slate-500">Chat sicura: email, numeri di telefono e link vengono automaticamente oscurati. Per incontrarsi usa la posizione sulla mappa.</p>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] bg-white border border-slate-200 rounded-2xl overflow-hidden h-[70vh]">
        {/* Sidebar */}
        <aside className={`border-r border-slate-200 overflow-y-auto ${cid ? "hidden md:block" : "block"}`}>
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">Nessuna conversazione. Apri un annuncio e clicca "Invia messaggio".</div>
          ) : (
            conversations.map(c => (
              <Link key={c.id} to={`/messaggi/${c.id}`} className={`flex gap-3 p-3 border-b border-slate-100 hover:bg-slate-50 ${cid === c.id ? "bg-orange-50" : ""}`}>
                <img src={c.other_user?.avatar || "https://i.pravatar.cc/80"} alt="" className="w-12 h-12 rounded-full"/>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-bold text-sm text-slate-900 truncate">{c.other_user?.name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{new Date(c.last_message_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{c.listing?.title}</div>
                  <div className="text-xs text-slate-400 truncate">{c.last_message_preview || "Nessun messaggio"}</div>
                </div>
              </Link>
            ))
          )}
        </aside>

        {/* Chat panel */}
        <section className={`flex flex-col ${!cid ? "hidden md:flex" : "flex"}`}>
          {!current ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <Lock className="w-6 h-6 text-slate-400"/>
                </div>
                <div className="text-sm">Seleziona una conversazione dalla lista</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="border-b border-slate-200 p-3 flex items-center gap-3 bg-white">
                <button onClick={() => navigate("/messaggi")} className="md:hidden p-2"><ArrowLeft className="w-4 h-4"/></button>
                <img src={current.other_user?.avatar} alt="" className="w-10 h-10 rounded-full"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm">{current.other_user?.name}</div>
                  {current.listing && (
                    <Link to={`/annuncio/${current.listing.id}`} className="text-xs text-[#e86a24] hover:underline truncate block">
                      {current.listing.title} · € {Number(current.listing.price).toLocaleString("it-IT")}
                    </Link>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
                <div className="flex items-center justify-center gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-4 py-1.5 mx-auto w-fit shadow-sm">
                  <Shield className="w-3.5 h-3.5 text-emerald-600"/>
                  Chat protetta. Numeri, email e link vengono nascosti.
                </div>
                {messages.map(m => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                        {m.type === "text" && (
                          <div className={`rounded-2xl px-3.5 py-2 text-sm break-words ${mine ? "bg-[#e86a24] text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                            {m.text}
                            {m.censored && (
                              <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "text-white/80" : "text-amber-600"}`}>
                                <Shield className="w-2.5 h-2.5"/> Dati sensibili oscurati
                              </div>
                            )}
                          </div>
                        )}
                        {m.type === "image" && (
                          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white max-w-[280px]">
                            <img src={resolveImg(m.image_url)} alt="" className="w-full object-cover"/>
                          </div>
                        )}
                        {m.type === "location" && <LocationMessage location={m.location} />}
                        <div className="text-[10px] text-slate-400 px-2">{new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* Input */}
              <div className="border-t border-slate-200 p-3 bg-white">
                <div className="flex items-center gap-2">
                  <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={sendImage} />
                  <button onClick={() => fileInput.current?.click()} className="p-2 rounded-full hover:bg-slate-100 text-slate-600" aria-label="Invia foto">
                    <ImageIcon className="w-5 h-5"/>
                  </button>
                  <button onClick={() => setLocOpen(true)} className="p-2 rounded-full hover:bg-orange-50 text-[#e86a24]" aria-label="Invia posizione">
                    <MapPin className="w-5 h-5"/>
                  </button>
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 h-10 rounded-full"
                    disabled={sending}
                  />
                  <Button onClick={sendText} disabled={!text.trim() || sending} className="rounded-full h-10 w-10 p-0 bg-[#e86a24] hover:bg-[#c8561a]">
                    <Send className="w-4 h-4"/>
                  </Button>
                </div>
                <div className="text-[10px] text-slate-400 mt-1.5 text-center">
                  Numeri di telefono, email e link sono oscurati automaticamente. Per incontrarsi, condividi la posizione dalla mappa.
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <LocationPicker open={locOpen} onClose={() => setLocOpen(false)} onShare={sendLocation} />
    </div>
  );
}
